import { useState, useCallback } from 'react';
import { validateImageFile } from '../components/aesthetic/ImageUpload';
import { readFileAsDataUrl, toDataUri } from '../utils/imageUtils';
import { simulateCosmeticSurgery } from '../services/simulatorApi';
import { DEFAULT_INTENSITY, DEFAULT_PROCEDURE_ID } from '../constants/procedures';

function getResultImage(results, field) {
  if (!results) return null;
  const raw = results[field];
  return raw ? toDataUri(raw) : null;
}

function getAfterImage(results) {
  if (!results) return null;
  const raw = results.after || results.simulatedImage || results.previewImage || results.result_image;
  return raw ? toDataUri(raw) : null;
}

function getBeforeImage(results, fallbackPreview) {
  const fromApi = getResultImage(results, 'before');
  return fromApi || fallbackPreview;
}

export function useSimulator() {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [procedure, setProcedure] = useState(DEFAULT_PROCEDURE_ID);
  const [intensity, setIntensity] = useState(DEFAULT_INTENSITY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const afterImage = getAfterImage(results);
  const beforeImage = getBeforeImage(results, imagePreview);

  const handleImageChange = useCallback(async (file) => {
    setFieldErrors((prev) => ({ ...prev, image: null }));
    setError('');
    setResults(null);

    if (!file) {
      setImageFile(null);
      setImagePreview(null);
      return;
    }

    const validationError = validateImageFile(file);
    if (validationError) {
      setFieldErrors((prev) => ({ ...prev, image: validationError }));
      return;
    }

    const preview = await readFileAsDataUrl(file);
    setImageFile(file);
    setImagePreview(preview);
  }, []);

  const handleProcedureChange = useCallback((procedureId) => {
    setProcedure(procedureId);
    setFieldErrors((prev) => ({ ...prev, procedure: null }));
    setResults(null);
  }, []);

  const handleIntensityChange = useCallback((value) => {
    setIntensity(value);
    setResults(null);
  }, []);

  const generatePreview = useCallback(async () => {
    const errors = {};
    if (!imageFile) errors.image = 'Patient image is required.';
    if (!procedure) errors.procedure = 'Please select a procedure.';

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setError('Please upload an image and select a procedure before generating.');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('procedure', procedure);
      formData.append('intensity', String(intensity));

      const data = await simulateCosmeticSurgery(formData);

      if (!data?.success) {
        throw new Error(data?.message || 'Simulation failed. Please try again.');
      }

      setResults(data);
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Unable to generate preview.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [imageFile, procedure, intensity]);

  const resetPreview = useCallback(() => {
    setResults(null);
    setError('');
  }, []);

  return {
    imageFile,
    imagePreview,
    procedure,
    intensity,
    fieldErrors,
    error,
    loading,
    results,
    beforeImage,
    afterImage,
    handleImageChange,
    handleProcedureChange,
    handleIntensityChange,
    generatePreview,
    resetPreview,
  };
}
